/**
 * cyberpunkPipeline — 3D raymarched city street with neon.
 *
 * A perspective view down a street lined with building blocks. Each
 * building has neon-lit windows, alternating magenta and cyan. The
 * street is wet (reflecting the neon). Atmospheric fog gives depth.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface CyberpunkPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

// SDF for a building block at position (cx, cz) with size (sx, sy, sz).
float sdBuilding(vec3 p, vec3 center, vec3 size) {
  vec3 q = abs(p - center) - size;
  return length(max(q, 0.0)) + min(max(q.x, max(q.y, q.z)), 0.0);
}

// Combined scene SDF with multiple buildings.
float sceneSDF(vec3 p) {
  // The street runs along Z. Buildings are placed on both sides at x = ±1.5.
  // Each building is a separate block with a different size.
  float d = 100.0;
  // Building cells at different z positions.
  for (int i = 0; i < 5; i++) {
    float zPos = -float(i) * 1.5 + 2.0;
    // Two buildings, one on each side.
    float sideSign = (mod(float(i), 2.0) < 0.5) ? 1.0 : -1.0;
    float heightVar = 0.8 + 0.6 * hash21(vec2(float(i), 1.0));
    float widthVar = 0.4 + 0.3 * hash21(vec2(float(i), 2.0));
    d = min(d, sdBuilding(p, vec3(sideSign * 1.5, heightVar * 0.5, zPos),
                          vec3(widthVar * 0.5, heightVar * 0.5, 0.6)));
  }
  // Ground plane.
  d = min(d, p.y);
  return d;
}

vec3 calcNormal(vec3 p) {
  vec2 e = vec2(0.002, 0.0);
  return normalize(vec3(
    sceneSDF(p + e.xyy) - sceneSDF(p - e.xyy),
    sceneSDF(p + e.yxy) - sceneSDF(p - e.yxy),
    sceneSDF(p + e.yyx) - sceneSDF(p - e.yyx)
  ));
}

// Check if a point is inside a "window" on a building face.
bool isWindow(vec3 p, vec3 buildingCenter, vec3 buildingSize, out vec3 windowColor) {
  // Building face normal points toward +X or -X (the street-facing sides).
  vec3 localPos = (p - buildingCenter) / buildingSize;
  // Window grid: 4 rows x 2 cols on each face.
  vec2 cell = floor(vec2(localPos.y * 4.0, mod(localPos.z + 100.0, 1.0) * 2.0));
  // Cell coords modulo for uniqueness.
  vec2 cellId = vec2(cell.x, cell.y + floor((p.z - buildingCenter.z) * 4.0));
  // Window: alternating colors (cyan / magenta) per cell.
  float isWindow = step(0.2, fract(cell.x * 0.5)) * step(0.2, fract(cell.y * 0.5));
  float h = hash21(cellId);
  windowColor = mix(
    vec3(0.95, 0.2, 0.85),  // magenta
    vec3(0.2, 0.95, 0.95),  // cyan
    fract(h * 1.7)
  );
  // Random cells are dark.
  if (h < 0.3) return false;
  return isWindow > 0.5;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected screen coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  // Camera setup: looking down a street.
  vec3 ro = vec3(0.0, 0.5, 4.0);
  vec3 ta = vec3(0.0, 0.3, -2.0 + sin(uTime * 0.3) * 0.5);
  vec3 cw = normalize(ta - ro);
  vec3 cu = normalize(cross(cw, vec3(0.0, 1.0, 0.0)));
  vec3 cv = cross(cu, cw);
  // Wide FOV.
  vec3 rd = normalize(p.x * cu + p.y * cv + 1.5 * cw);

  // Raymarch.
  float td = 0.0;
  bool hit = false;
  vec3 hitP = ro;
  for (int i = 0; i < 64; i++) {
    vec3 p2 = ro + rd * td;
    float d = sceneSDF(p2);
    if (d < 0.005) { hit = true; hitP = p2; break; }
    td += d * 0.9;
    if (td > 30.0) break;
  }

  vec3 col;
  if (hit) {
    vec3 n = calcNormal(hitP);
    // Is the hit on a building face?
    if (hitP.y > 0.05) {
      // Building wall.
      // Window test (approximate — check which building face).
      vec3 winColor;
      bool isWin = false;
      // Find which building: walk through them.
      for (int i = 0; i < 5; i++) {
        float zPos = -float(i) * 1.5 + 2.0;
        float sideSign = (mod(float(i), 2.0) < 0.5) ? 1.0 : -1.0;
        float heightVar = 0.8 + 0.6 * hash21(vec2(float(i), 1.0));
        float widthVar = 0.4 + 0.3 * hash21(vec2(float(i), 2.0));
        vec3 bC = vec3(sideSign * 1.5, heightVar * 0.5, zPos);
        vec3 bS = vec3(widthVar * 0.5, heightVar * 0.5, 0.6);
        // Check if this building is the one we hit.
        vec3 lp = (hitP - bC) / bS;
        if (abs(lp.x) > 0.95) {
          isWin = isWindow(hitP, bC, bS, winColor);
          break;
        }
      }
      if (isWin) {
        // Glowing window.
        col = winColor * (1.5 + high * 1.0);
        // Bloom around windows.
        col += winColor * 0.3;
      } else {
        // Dark building.
        col = vec3(0.05, 0.06, 0.08);
        // Subtle ambient.
        col += vec3(0.02, 0.03, 0.05);
      }
    } else {
      // Wet street: dark with neon reflection.
      col = vec3(0.02, 0.03, 0.06);
      // Approximate reflection: sample neon color from above.
      float refl = exp(-hitP.z * 0.3);
      col += vec3(0.5, 0.2, 0.5) * refl * 0.3;
      col += vec3(0.2, 0.5, 0.5) * refl * 0.3;
    }
    // Atmospheric fog (deeper = darker, with magenta tint).
    float fogAmount = 1.0 - exp(-td * 0.05);
    col = mix(col, vec3(0.08, 0.04, 0.10), fogAmount);
  } else {
    // Sky: dark with starfield.
    vec2 starUv = floor(gl_FragCoord.xy / 4.0);
    float starSeed = hash21(starUv);
    float starMask = step(0.999, starSeed);
    col = vec3(0.02, 0.02, 0.05) + vec3(0.8, 0.85, 1.0) * starMask * 0.3;
  }

  // Vignette.
  vec2 vp = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float vig = 1.0 - dot(vp, vp) * 0.3;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeCyberpunkBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.6);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.9 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.2 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.4 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.3 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.7 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.2 + 3.0);
}

export function computeCyberpunkRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createCyberpunkPipeline(gl: WebGL2RenderingContext): CyberpunkPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawCyberpunk(
  gl: WebGL2RenderingContext,
  pipe: CyberpunkPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
